/**
 * Protected Portal Route Component
 *
 * Story: 11-1-portal-de-acesso-externo
 * Task: Frontend - PortalLayout Component
 *
 * Route guard for portal pages
 * - Checks portal authentication status
 * - Redirects to portal login if not authenticated
 * - Shows loading state during auth check
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { Loader2 } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

interface ProtectedPortalRouteProps {
  children: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

const ProtectedPortalRoute: React.FC<ProtectedPortalRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = usePortalAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-2 text-sm text-gray-600">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  // Redirect to portal login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  // Senha temporária (1º acesso): bloqueia o portal até trocar a senha.
  if (user?.password_change_required) {
    return <Navigate to="/portal/trocar-senha" replace />;
  }

  // Render protected content
  return <>{children}</>;
};

export default ProtectedPortalRoute;
